/**
 * plans.ts — แพ็กเกจและการจำกัดสิทธิ์ (config เดียวใช้ทั้ง web + api)
 *
 * ⚠️ ราคา/limit เป็น "ค่าตั้งต้นเชิงออกแบบ" (docs/billing-and-plans.md) ยังไม่ล็อกเชิงธุรกิจ
 * เก็บเป็น config ที่นี่เพื่อให้ web และ Worker ใช้กติกาชุดเดียว (แพทเทิร์นเดียวกับ PNL labels)
 * การบังคับจริงต้องทำที่ Worker เสมอ — UI แค่ซ่อน/ล็อกเพื่อ UX
 */

export type Plan = 'trial' | 'starter' | 'growth' | 'business';
export type PlanStatus = 'trialing' | 'active' | 'past_due' | 'canceled';

/** โมดูลขั้นสูงที่ gate ตามแพ็กเกจ */
export type GatedModule = 'payroll' | 'assets' | 'tax' | 'inventory' | 'allocation';

export const PLAN_ORDER: Plan[] = ['trial', 'starter', 'growth', 'business'];

export interface PlanLimits {
  label: string;
  /** ราคา บาท/เดือน (ยังไม่รวม VAT) — ค่าตั้งต้น */
  pricePerMonth: number;
  /** จำนวนกิจการ (business); null = ไม่จำกัด (fair-use) */
  businesses: number | null;
  /** จำนวนร้าน (shop); null = ไม่จำกัด */
  shops: number | null;
  /** จำนวนผู้ใช้ต่อบัญชี; null = ไม่จำกัด */
  users: number | null;
  /** โมดูลขั้นสูงที่เปิดใช้ */
  modules: GatedModule[];
  /** ประวัติย้อนหลังที่ query ได้ (เดือน); null = ไม่จำกัด */
  historyMonths: number | null;
  /** โควตาไฟล์แนบ (ไบต์) */
  storageBytes: number;
  /** ช่องทางที่ซิงค์ผ่าน API อัตโนมัติได้ */
  autoChannels: string[];
}

const GB = 1024 * 1024 * 1024;
const ALL_MODULES: GatedModule[] = ['payroll', 'assets', 'tax', 'inventory', 'allocation'];

/** ตารางแพ็กเกจ — ปรับได้โดยไม่แตะโค้ด enforcement */
export const PLAN_LIMITS: Record<Plan, PlanLimits> = {
  trial: {
    label: 'ทดลองใช้',
    pricePerMonth: 0,
    businesses: 1,
    shops: 1,
    users: 3,
    modules: ALL_MODULES, // ช่วงทดลองเปิดครบเพื่อให้ลองทุกฟีเจอร์
    historyMonths: null,
    storageBytes: 1 * GB,
    autoChannels: ['tiktok_shop'],
  },
  starter: {
    label: 'Starter',
    pricePerMonth: 590,
    businesses: 1,
    shops: 1,
    users: 1,
    modules: [],
    historyMonths: 12,
    storageBytes: 2 * GB,
    autoChannels: ['tiktok_shop'],
  },
  growth: {
    label: 'Growth',
    pricePerMonth: 1490,
    businesses: 3,
    shops: 5,
    users: 3,
    modules: ['payroll', 'assets', 'tax', 'inventory'],
    historyMonths: 24,
    storageBytes: 10 * GB,
    autoChannels: ['tiktok_shop', 'shopee', 'lazada'],
  },
  business: {
    label: 'Business',
    pricePerMonth: 3900,
    businesses: null,
    shops: null,
    users: null,
    modules: ALL_MODULES,
    historyMonths: null,
    storageBytes: 50 * GB,
    autoChannels: ['tiktok_shop', 'shopee', 'lazada', 'facebook', 'line'],
  },
};

/**
 * แพ็กเกจเริ่มต้นเมื่อบัญชีไม่มีค่า plan (บัญชีเดิม/ก่อน migration)
 * ตั้งเป็น business เพื่อ **fail-open** — ไม่บล็อกผู้ใช้เดิมโดยไม่ตั้งใจ
 * (paywall เปิดผลจริงเมื่อ account ได้ plan ที่ต่ำกว่านี้)
 */
export const DEFAULT_PLAN: Plan = 'business';

export function planLimits(plan: Plan | null | undefined): PlanLimits {
  return PLAN_LIMITS[plan ?? DEFAULT_PLAN] ?? PLAN_LIMITS[DEFAULT_PLAN];
}

/** โมดูลขั้นสูงนี้เปิดใช้ในแพ็กเกจหรือไม่ */
export function moduleAllowed(plan: Plan | null | undefined, mod: GatedModule): boolean {
  return planLimits(plan).modules.includes(mod);
}

/** จะเชื่อม/สร้างร้านเพิ่มได้ไหม (currentCount = จำนวนร้านปัจจุบัน) */
export function canAddShop(plan: Plan | null | undefined, currentCount: number): boolean {
  const lim = planLimits(plan).shops;
  return lim === null || currentCount < lim;
}

/** จะเพิ่มกิจการได้ไหม */
export function canAddBusiness(plan: Plan | null | undefined, currentCount: number): boolean {
  const lim = planLimits(plan).businesses;
  return lim === null || currentCount < lim;
}

/** ช่องทางนี้ซิงค์ API อัตโนมัติได้ไหม */
export function autoChannelAllowed(plan: Plan | null | undefined, channel: string): boolean {
  return planLimits(plan).autoChannels.includes(channel);
}

/**
 * clamp วันที่เริ่มของรายงานตามประวัติย้อนหลังที่แพ็กเกจให้
 * คืน from ที่ปรับแล้ว (ไม่ให้ query ก่อน earliestAllowed); ข้อมูลเก่าไม่ถูกลบ แค่ query ไม่ได้
 * @param todayKey วันนี้รูป YYYY-MM-DD (Asia/Bangkok)
 */
export function clampHistoryFrom(
  plan: Plan | null | undefined,
  from: string,
  todayKey: string,
): { from: string; clamped: boolean } {
  const months = planLimits(plan).historyMonths;
  if (months === null) return { from, clamped: false };
  const earliest = earliestHistoryKey(todayKey, months);
  if (from < earliest) return { from: earliest, clamped: true };
  return { from, clamped: false };
}

/** YYYY-MM-DD ของ (todayKey − months เดือน), วันที่ 1 ของเดือนนั้น */
export function earliestHistoryKey(todayKey: string, months: number): string {
  const [y, m] = todayKey.split('-').map(Number) as [number, number, number];
  // เดือนปัจจุบัน index (y*12 + (m-1)) − months
  const idx = y * 12 + (m - 1) - months;
  const ey = Math.floor(idx / 12);
  const em = (idx % 12) + 1;
  return `${ey}-${String(em).padStart(2, '0')}-01`;
}

/** โควตาไฟล์ (ไบต์) ของแพ็กเกจ */
export function storageQuota(plan: Plan | null | undefined): number {
  return planLimits(plan).storageBytes;
}

/** เพิ่มไฟล์ขนาดนี้แล้วยังอยู่ในโควตาไหม */
export function withinStorageQuota(
  plan: Plan | null | undefined,
  usedBytes: number,
  addBytes: number,
): boolean {
  return usedBytes + addBytes <= storageQuota(plan);
}

export const MODULE_LABELS: Record<GatedModule, string> = {
  payroll: 'เงินเดือน (Payroll)',
  assets: 'ทรัพย์สินและค่าเสื่อม',
  tax: 'ภาษีและรายการปรับปรุง',
  inventory: 'ซื้อสินค้าและสต็อก',
  allocation: 'การปันส่วนค่าใช้จ่าย',
};
